import { Button, Checkbox, Space, Table, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import ReactDragListView from 'react-drag-listview';

import sharedStyles from '../../sharedStyles.module.scss';
import { initPaidItem } from '../constants';
import type { FreeItemTableData, PaidItemTableData } from '../types';
import {
  checkIsAllItemFieldsEmpty,
  checkIsAllPaidItemFieldsEmpty,
  checkIsPaidItemValuesPassed,
  getFreeItemColumns,
  getPaidItemTableColumns,
  restoreEditingItemValuesWhenDelete,
  updatePaidItemOrder,
} from './helper';
import styles from './index.module.scss';

type CleanKindItemsProps = {
  type: 'add' | 'edit';
  ticketOptions: { label: string; value: string }[] | undefined;
  freeItem: FreeItemTableData[];
  paidItems: PaidItemTableData[];
  updateFreeItem: React.Dispatch<React.SetStateAction<FreeItemTableData[]>>;
  updatePaidItems: React.Dispatch<React.SetStateAction<PaidItemTableData[]>>;
  isValidating: boolean;
  updateIsValidating: React.Dispatch<React.SetStateAction<boolean>>;
  updateIsAllCleanItemsValidated: (isAllItemsPassed: boolean) => void;
};

const { Text } = Typography;

const CleanKindItems = ({
  type,
  ticketOptions,
  freeItem,
  paidItems,
  updateFreeItem,
  updatePaidItems,
  isValidating,
  updateIsValidating,
  updateIsAllCleanItemsValidated,
}: CleanKindItemsProps) => {
  const [isCheckTaiwanPay, setIsCheckTaiwanPay] = useState(false);
  const [isCheckPoint, setIsCheckPoint] = useState(false);

  const isAllPaidItemFieldsEmpty = checkIsAllPaidItemFieldsEmpty({
    paidItems,
    isCheckTaiwanPay,
    isCheckPoint,
  });

  const isAllItemFieldsEmpty = checkIsAllItemFieldsEmpty({
    freeItem,
    paidItems,
    isCheckTaiwanPay,
    isCheckPoint,
  });

  useEffect(() => {
    if (paidItems[0].taiwanPayPrice) {
      setIsCheckTaiwanPay(true);
    }
    if (paidItems[0].pointPrice) {
      setIsCheckPoint(true);
    }
  }, [paidItems]);

  useEffect(() => {
    if (isValidating && isAllItemFieldsEmpty) {
      updateIsAllCleanItemsValidated(false);
    }
    if (isValidating && !isAllItemFieldsEmpty) {
      const isAllFreeItemFieldsEmpty =
        !freeItem[0].itemName && freeItem[0].ticketId.length === 0;
      const isFreeItemValuesPassed =
        Boolean(freeItem[0].itemName) && freeItem[0].ticketId.length !== 0;
      const isPaidItemValuesPassed = checkIsPaidItemValuesPassed({
        paidItems,
        isCheckTaiwanPay,
        isCheckPoint,
      });

      const isAllItemsPassed =
        (isAllFreeItemFieldsEmpty && isPaidItemValuesPassed) ||
        (isAllPaidItemFieldsEmpty && isFreeItemValuesPassed) ||
        (!isAllFreeItemFieldsEmpty &&
          !isAllPaidItemFieldsEmpty &&
          isFreeItemValuesPassed &&
          isPaidItemValuesPassed);

      updateIsAllCleanItemsValidated(isAllItemsPassed);
    }
  }, [
    freeItem,
    isAllItemFieldsEmpty,
    isAllPaidItemFieldsEmpty,
    isCheckPoint,
    isCheckTaiwanPay,
    isValidating,
    paidItems,
    updateIsAllCleanItemsValidated,
  ]);

  const handleDragEnd = (fromIndex: number, toIndex: number) => {
    const sortedPaidItems = updatePaidItemOrder(fromIndex, toIndex, paidItems);
    updatePaidItems(sortedPaidItems);
  };

  const addPaidItem = () => {
    const newItem = {
      ...initPaidItem,
      itemId: 'item' + Math.random().toString(16),
    };
    if (isValidating) {
      updateIsValidating(false);
    }
    updatePaidItems([...paidItems, newItem]);
  };

  const paidItemsUpdater = (args: {
    itemId: string;
    value: string | number | null;
    dataIndex: string;
  }) => {
    const { itemId, value, dataIndex } = args;
    if (isValidating) {
      updateIsValidating(false);
    }
    updatePaidItems((prevItems) => {
      let newPaidItems = [...prevItems];
      if (
        dataIndex === 'reservationLowerLimit' ||
        dataIndex === 'reservationUpperLimit'
      ) {
        newPaidItems = newPaidItems.map((item) => {
          return item.itemId === itemId
            ? {
                ...item,
                reservationLimits: {
                  ...item.reservationLimits,
                  [dataIndex]: value,
                },
              }
            : item;
        });
      } else {
        newPaidItems = newPaidItems.map((item) => {
          return item.itemId === itemId
            ? {
                ...item,
                [dataIndex]: value,
              }
            : item;
        });
      }

      return newPaidItems;
    });
  };

  const updateCheckTaiwanPay = () => {
    if (isValidating) {
      updateIsValidating(false);
    }
    if (isCheckTaiwanPay) {
      const updatedItems = paidItems.map((item) => ({
        ...item,
        taiwanPayPrice: null,
      }));
      updatePaidItems(updatedItems);
    }
    setIsCheckTaiwanPay((prevState) => !prevState);
  };

  const updateCheckPoint = () => {
    if (isValidating) {
      updateIsValidating(false);
    }
    if (isCheckPoint) {
      const updatedItems = paidItems.map((item) => ({
        ...item,
        pointPrice: null,
      }));
      updatePaidItems(updatedItems);
    }
    setIsCheckPoint((prevState) => !prevState);
  };

  const deletePaidItem = (record: PaidItemTableData) => {
    const copiedPaidItems = [...paidItems];
    const isAddedItem = record.itemId.includes('item');
    const isItemValuesPassed = checkIsPaidItemValuesPassed({
      paidItems: [record],
      isCheckTaiwanPay,
      isCheckPoint,
    });

    let wantDeleteItem = { ...record };

    if (isItemValuesPassed === false) {
      wantDeleteItem = restoreEditingItemValuesWhenDelete(wantDeleteItem);
    }

    let newPaidItems: PaidItemTableData[] = [];

    if (type === 'edit' && isAddedItem === false) {
      newPaidItems = copiedPaidItems.map((item) => {
        return item.itemId === record.itemId &&
          item.itemId.includes('item') === false
          ? {
              ...wantDeleteItem,
              display: false,
            }
          : item;
      });
    } else {
      newPaidItems = copiedPaidItems.filter(
        (item) => item.itemId !== record.itemId
      );
    }

    const isAllItemsDisplayFalse = newPaidItems.every(
      (item) => item.display === false
    );

    updatePaidItems(
      newPaidItems.length !== 0 && isAllItemsDisplayFalse
        ? [...newPaidItems, initPaidItem]
        : newPaidItems.length !== 0 && isAllItemsDisplayFalse === false
        ? newPaidItems
        : [initPaidItem]
    );
  };

  const freeItemColumns = getFreeItemColumns({
    ticketOptions,
    updateFreeItem,
    isValidating,
    isAllItemFieldsEmpty,
    updateIsValidating,
  });

  const paidItemTableColumns = getPaidItemTableColumns({
    updatePaidItems: paidItemsUpdater,
    isValidating,
    isCheckTaiwanPay,
    isCheckPoint,
    isAllItemFieldsEmpty,
    paidItems,
    deletePaidItem,
  });

  return (
    <Space
      direction="vertical"
      size="middle"
      className={sharedStyles.widthFull}
    >
      <h4 className={sharedStyles.required}>編輯類別項目</h4>
      <Space direction="vertical" className={sharedStyles.widthFull}>
        <Space direction="vertical" size={0}>
          <Text type="secondary">免費項目（票卷扣抵）</Text>
          {isValidating && isAllItemFieldsEmpty ? (
            <Text type="danger">請至少填寫一種</Text>
          ) : (
            <div className={styles.textPlaceholder} />
          )}
        </Space>
        <Table
          bordered
          columns={freeItemColumns}
          dataSource={freeItem}
          rowKey="ticketId"
          pagination={false}
        />
      </Space>
      <Space direction="vertical" className={sharedStyles.widthFull}>
        <Space direction="vertical" size={0}>
          <Text type="secondary">付費項目</Text>
          {isValidating && isAllItemFieldsEmpty ? (
            <Text type="danger">請至少填寫一種</Text>
          ) : (
            <div className={styles.textPlaceholder} />
          )}
        </Space>
        <Space className={styles.spaceBetween} align="start">
          <Button onClick={addPaidItem}>新增清潔項目</Button>
          <Space align="start">
            <Space direction="vertical" size={0}>
              <Text type="secondary" className={sharedStyles.required}>
                開放付費方式：
              </Text>
              {isValidating &&
              !isAllPaidItemFieldsEmpty &&
              !isCheckTaiwanPay &&
              !isCheckPoint ? (
                <Text type="danger">請勾選任一種</Text>
              ) : (
                <div className={styles.textPlaceholder} />
              )}
            </Space>
            <Checkbox
              checked={isCheckTaiwanPay}
              onChange={updateCheckTaiwanPay}
            >
              台灣Pay
            </Checkbox>
            <Checkbox checked={isCheckPoint} onChange={updateCheckPoint}>
              點數
            </Checkbox>
          </Space>
        </Space>
        <ReactDragListView onDragEnd={handleDragEnd} handleSelector="svg">
          <Table
            bordered
            columns={paidItemTableColumns}
            dataSource={paidItems.filter(
              (item) =>
                item.display ||
                (item.itemId.includes('item') && item.display !== false)
            )}
            rowKey="itemId"
            pagination={false}
          />
        </ReactDragListView>
      </Space>
    </Space>
  );
};

export default CleanKindItems;
